import React from "react";
import { FaInstagram } from "react-icons/fa"
import { Link } from "react-router-dom";
export default function Mentalhealthguide() {
  return (
    <section className="relative bg-[#02042b] text-white mt-12">
      {/* subtle glow accents */}
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          background:
            "radial-gradient(900px 400px at 10% -10%, rgba(99,102,241,.18), transparent 60%), radial-gradient(800px 360px at 120% 0%, rgba(236,72,153,.15), transparent 55%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {/* Hero */}
        <header className="text-center max-w-3xl mx-auto mb-10">
          <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight">
            Mental Health in Education: Stress, Anxiety & Coping Mechanisms
          </h1>
          <p className="mt-3 text-gray-300">
            Mental Health in Education: reduce stress and anxiety with CBT, mindfulness, sleep hygiene, and smart exam strategies for calmer, better learning.
          </p>
          <div className="mt-4 inline-flex items-center gap-3 text-xs text-gray-300/90 bg-white/5 border border-white/10 rounded-full px-4 py-2">
            <span className="font-semibold">URL slug:</span>
            <code className="text-rose-300">/mental-health-in-education</code>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* TOC (sticky on desktop) */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 bg-white/5 backdrop-blur rounded-xl border border-white/10 p-5">
              <h2 className="text-lg font-semibold mb-3">Table of Contents</h2>
              <nav className="text-sm text-gray-300 space-y-2">
                <a href="#overview" className="block hover:text-white">Overview</a>
                <a href="#keypoints" className="block hover:text-white">5 Key Points</a>
                <a href="#data" className="block hover:text-white">At-a-Glance Data</a>
                <a href="#drivers" className="block hover:text-white">What’s Driving Stress & Anxiety</a>
                <a href="#signs" className="block hover:text-white">How It Shows Up</a>
                <a href="#coping" className="block hover:text-white">Coping Mechanisms</a>
                <a href="#sleepreset" className="block hover:text-white">7-Day Sleep Reset</a>
                <a href="#moment" className="block hover:text-white">In-the-Moment Tools</a>
                <a href="#rewiring" className="block hover:text-white">Long-Term Rewiring</a>
                <a href="#study" className="block hover:text-white">Study Strategies</a>
                <a href="#exam" className="block hover:text-white">Exam Pressure Playbook</a>
                <a href="#digital" className="block hover:text-white">Digital Wellbeing (30 Days)</a>
                <a href="#equity" className="block hover:text-white">Equity & Access</a>
                <a href="#mtss" className="block hover:text-white">MTSS (School Systems)</a>
                <a href="#policy" className="block hover:text-white">Policy & Privacy</a>
            
                <a href="#faqs" className="block hover:text-white">FAQs</a>
                <a href="#help" className="block hover:text-white">How We Help</a>
                <a href="#conclusion" className="block hover:text-white">Conclusion</a>
                 <a href="#acknowledgement" className="block hover:text-white">Acknowledgement</a>
              </nav>
            </div>
          </aside>

          {/* Article */}
          <article className="lg:col-span-8 space-y-10">
            {/* Overview */}
            <section id="overview" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Overview</h2>
              <p className="text-gray-200">
                Mental Health in Education is the everyday link between how students feel and how well they learn. When stress and anxiety climb through exam pressure, social comparison, or money worries, attention narrows, memory slips, and motivation fades.
              </p>
              <p className="text-gray-200 mt-3">
                The fix isn’t willpower; it’s teaching simple, evidence-based coping mechanisms: quick CBT thought audits, five-minute mindfulness, consistent sleep hygiene, and practical digital wellbeing habits.
              </p>
              <p className="text-gray-200 mt-3">
                These build resilience, make it easier to talk with a school counselor, and protect study-life balance so progress feels doable, not draining.
              </p>
            </section>

            {/* Key Points */}
            <section id="keypoints" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-4">5 Key Points</h2>
              <ul className="list-disc pl-6 space-y-3 text-gray-200">
                <li>
                  Students face predictable drivers of stress and anxiety, academic load, social comparison, transitions, neurodiversity needs, and trauma which show up as cognitive, physical, behavioral, and academic signs you can spot early.
                </li>
                <li>
                  Actionable coping mechanisms work: foundations (sleep, movement, boundaries), in-the-moment tools (breathing, grounding, PMR), and long-term rewiring (CBT, self-compassion, metacognition).
                </li>
                <li>
                  Smart study systems reduce exam pressure: active recall, spaced repetition, task chunking, a 7-day exam playbook, and routines that protect study-life balance.
                </li>
                <li>
                  Whole-school frameworks matter: MTSS/UDL, clear referral pathways to a school counselor or school psychologist, equitable accommodations (e.g., ADHD/autism), and privacy basics (GDPR/FERPA/SEND).
                </li>
                <li>
                  Modern habits amplify outcomes: purposeful digital wellbeing, measurable goals (attendance, completion, test-anxiety scores), practical toolkits, and FAQs that normalize help-seeking and build resilience.
                </li>
              </ul>
            </section>

            {/* Data */}
            <section id="data" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">At-a-Glance Data: Student Mental Health Today</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li>Around one in seven adolescents live with a mental health condition, with anxiety among the most common.</li>
                <li>Suicide is among the leading causes of death for older adolescents; early identification and help-seeking save lives.</li>
                <li>Surveys in several regions report a sharp rise in persistent sadness, sleep disruption, and test anxiety since the pandemic.</li>
                <li>Night-time screen exposure, irregular sleep, and low physical activity correlate with lower academic performance.</li>
                <li>Access matters: students who know how to reach a school counselor or counseling services use support earlier—and outcomes improve.</li>
              </ul>
            </section>

            {/* Drivers */}
            <section id="drivers" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6 space-y-4">
              <h2 className="text-2xl font-bold mb-2">What’s Driving Stress & Anxiety Right Now?</h2>
              <div>
                <h3 className="font-semibold text-white">Academic load + perfectionism</h3>
                <p className="text-gray-200">Heavy assignment cycles and high-stakes tests can fuel perfectionism (“anything less than perfect is failure”), leading to procrastination, rumination, and burnout. Reframing with a growth mindset and self-efficacy (“I can learn this with the right strategy”) reduces pressure.</p>
              </div>
              <div>
                <h3 className="font-semibold text-white">Social comparison & the scroll</h3>
                <p className="text-gray-200">Social media is a mixed bag: connection vs. comparison. Constant notifications, late-night scrolling, and cyberbullying strain attention and sleep hygiene, raising baseline anxiety.</p>
              </div>
              <div>
                <h3 className="font-semibold text-white">Transitions & identity</h3>
                <p className="text-gray-200">New schools, university starts, moving countries, uncertainty spikes stress. International and first-generation students often face financial stress, language barriers, and imposter syndrome.</p>
              </div>
              <div>
                <h3 className="font-semibold text-white">Neurodiversity and hidden barriers</h3>
                <p className="text-gray-200">Students with ADHD, autism, dyslexia, or dyscalculia may need reasonable adjustments and Universal Design for Learning (UDL). Without them, effort ≠ outcome—and anxiety climbs.</p>
              </div>
              <div>
                <h3 className="font-semibold text-white">Trauma, stigma, and money</h3>
                <p className="text-gray-200">Past or ongoing trauma, stigma around mental health, and tight finances undermine focus, safety, and resilience. Clear, non-judgmental pathways to help are essential.</p>
              </div>
            </section>

            {/* Signs */}
            <section id="signs" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">How Stress & Anxiety Show Up (and When to Worry)</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li><strong>Cognitive:</strong> racing thoughts, catastrophizing, poor working memory</li>
                <li><strong>Physical:</strong> headaches, stomachaches, rapid heartbeat, fatigue</li>
                <li><strong>Behavioral:</strong> avoidance, over-editing, irritability, withdrawal</li>
                <li><strong>Academic:</strong> attendance dips, last-minute cramming, all-or-nothing patterns</li>
              </ul>
              <p className="text-rose-300/90 mt-3 font-medium">Get urgent help for self-harm thoughts, sudden severe mood shifts, substance misuse, or total disengagement. Early support beats crisis response.</p>
            </section>

            {/* Coping Overview */}
            <section id="coping" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Coping Mechanisms That Actually Help</h2>
              <p className="text-gray-200">Think in three layers: Foundations, In-the-Moment Tools, and Long-Term Rewiring.</p>
            </section>

            {/* Foundations */}
            <section id="foundations" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h3 className="text-xl font-semibold mb-2">A) Foundations (protect your baseline)</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li><strong>Sleep hygiene (7-9 hours):</strong> fixed bed/wake times; dim lights/screens 60 minutes before bed; cool, dark room; phone outside the bedroom.</li>
                <li><strong>Movement:</strong> 20–30 minutes of physical activity (walk, strength, yoga) most days—proven lift for mood and focus.</li>
                <li><strong>Nutrition & hydration:</strong> stable energy beats sugar spikes; pair carbs with protein; keep a water bottle nearby.</li>
                <li><strong>Study-life boundaries:</strong> clear start/stop rituals and one phone-free hour before bed to protect study-life balance.</li>
              </ul>
            </section>

            {/* Sleep Reset */}
            <section id="sleepreset" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h3 className="text-xl font-semibold mb-2">7-Day Sleep Reset (mini plan)</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li><strong>Day 1:</strong> Choose a fixed wake time; no naps after 3 pm.</li>
                <li><strong>Day 2:</strong> 15-minute daylight walk within 2 hours of waking.</li>
                <li><strong>Day 3:</strong> Caffeine cutoff 8 hours before bed.</li>
                <li><strong>Day 4:</strong> 60-minute screen gap pre-bed; use a paperback.</li>
                <li><strong>Day 5:</strong> PM wind-down: mindfulness (5 min) + PMR (5 min).</li>
                <li><strong>Day 6:</strong> Gentle stretch + warm shower 60–90 minutes before bed.</li>
                <li><strong>Day 7:</strong> Review: Did sleep latency and energy improve?</li>
              </ul>
            </section>

            {/* In the Moment */}
            <section id="moment" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h3 className="text-xl font-semibold mb-2">B) In-the-Moment Tools (downshift fast)</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li>Box breathing (4-4-4-4): 1–3 minutes.</li>
                <li>5-4-3-2-1 grounding: re-anchor senses when spiraling.</li>
                <li>Progressive Muscle Relaxation (PMR): tense→release head-to-toe.</li>
                <li>Pomodoro with micro-breaks: 25 on, 5 off; break = stand, breathe, sip water (not scroll).</li>
                <li>STOP skill: Stop, Take a breath, Observe, Proceed intentionally.</li>
              </ul>
            </section>

            {/* Rewiring */}
            <section id="rewiring" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h3 className="text-xl font-semibold mb-2">C) Long-Term Rewiring</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li><strong>CBT thought audit (ABCDE):</strong> Activating event → Belief (“If I don’t ace this, I’m a failure”) → Consequences (panic/avoid) → Dispute (Is that true? Evidence?) → Energize (balanced belief + next step).</li>
                <li><strong>Self-compassion:</strong> talk to yourself as you would a friend; lowers perfectionism and boosts resilience.</li>
                <li><strong>Metacognition for studying:</strong> weekly review—what worked, what didn’t? Shift methods rather than studying harder.</li>
              </ul>
            </section>

            {/* Study Strategies */}
            <section id="study" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Study Strategies that Reduce Stress</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li>Active recall over re-reading; teach a friend, quiz yourself.</li>
                <li>Spaced repetition across days/weeks.</li>
                <li>Task chunking: “Find 3 sources” → “Summarize one paragraph each.”</li>
                <li>Timed mock tests reduce test anxiety.</li>
                <li>Environment design: clean, quiet, single-tasking beats multitasking.</li>
              </ul>
            </section>

            {/* Exam Playbook */}
            <section id="exam" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Exam Pressure Playbook (7-Day Countdown)</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
       <li>
  <strong>Day -7:</strong> Map topics; pick 3 priority areas.{" "}
  <Link 
    to="/blog/academicassignmentstructureguide" 
    className="text-indigo-400 font-semibold hover:text-indigo-300 underline"
  >
    Structure your assignments
  </Link>{" "}
  across the week.
</li>

                <li><strong>Day -6:</strong> Past paper (timed) on hardest topic; mark errors.</li>
                <li><strong>Day -5:</strong> CBT thought audit on your top 2 worries; write balanced counters.</li>
                <li><strong>Day -4:</strong> Mindfulness 5 minutes before each block; 20-minute walk after dinner.</li>
                <li><strong>Day -3:</strong> Sleep drill: full wind-down routine + PMR; screen gap = 60 minutes.</li>
                <li><strong>Day -2:</strong> Teach a peer one topic (active recall); pack exam kit (ID, pens, water).</li>
                <li><strong>Day -1:</strong> Light review only; 30 minutes movement; early night.</li>
              </ul>
              <div className="mt-3 text-gray-200">
                <p className="font-semibold">Morning-of Script (2 minutes):</p>
                <ol className="list-decimal pl-6 space-y-1">
                  <li>4 box breaths.</li>
                  <li>Say: “I know enough to start; I’ll take one question at a time.”</li>
                  <li>Eat something simple (protein + complex carbs).</li>
                  <li>Arrive early; no last-minute doomscrolling.</li>
                </ol>
              </div>
            </section>

            {/* Digital Wellbeing */}
            <section id="digital" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Digital Wellbeing (30-Day Challenge)</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li><strong>Week 1 – Audit:</strong> Track screen time; mute all non-essential notifications; put socials in a folder off the home screen.</li>
                <li><strong>Week 2 – Limits:</strong> App timers (even 20% less helps); grayscale after 8 pm; study in Do Not Disturb.</li>
                <li><strong>Week 3 – Sleep:</strong> 60-minute phone-free buffer before bed; charge outside the bedroom; swap scroll for mindfulness or journaling.</li>
                <li><strong>Week 4 – Purpose:</strong> Begin each app session by stating a purpose (“message X, 5 minutes”); unfollow comparison triggers; follow 3 accounts that teach, not drain.</li>
              </ul>
              <p className="text-gray-200 mt-3">If cyberbullying occurs: screenshot, block, report, and share with a trusted adult or school counselor; schools should have a clear safeguarding response.</p>
            </section>

            {/* Equity & Access */}
            <section id="equity" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Equity & Access: Who Needs What (and Why)</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li>High-risk/underserved groups: international students, first-gen, LGBTQ+ youth, students facing financial stress, and those with ADHD, autism, or learning differences.</li>
                <li>Culturally responsive care: interpreter access, inclusive examples, community-aligned supports.</li>
                <li>Reasonable adjustments: extra time, quiet rooms, rest breaks, alternative formats, especially during exam pressure periods.</li>
              </ul>
            </section>

            {/* MTSS */}
            <section id="mtss" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">How Schools Make Support Systematic (MTSS)</h2>
              <p className="text-gray-200">Multi-Tiered System of Supports (MTSS) organizes care so every student gets what they need:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-200 mt-2">
                <li><strong>Tier 1 (Universal):</strong> whole-school mental health literacy, SEL, predictable routines, anti-bullying policies, staff training, clear referrals, family communication.</li>
                <li><strong>Tier 2 (Targeted):</strong> small-group CBT, mentoring, check-ins, targeted study-life balance coaching.</li>
                <li><strong>Tier 3 (Intensive):</strong> individualized plans, school psychologist involvement, external therapist/teletherapy, crisis planning.</li>
              </ul>
            </section>

            {/* Policy & Privacy */}
            <section id="policy" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Policy & Privacy (Plain-English Overview)</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li><strong>US:</strong> FERPA protects education records; 504 plans and IEPs outline accommodations.</li>
                <li><strong>UK/EU:</strong> GDPR governs data; the UK framework includes SEND and “reasonable adjustments.”</li>
                <li><strong>Everywhere:</strong> publish what is confidential, what triggers safeguarding, and how to contact counseling services.</li>
              </ul>
            </section>

            {/* Measure */}
            <section id="measure" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Measure What Matters (for Schools & Families)</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li><strong>Inputs:</strong> staff trained, lessons delivered, referrals made</li>
                <li><strong>Student indicators:</strong> attendance, assignment completion, test anxiety scores (pre/post), self-report wellbeing</li>
                <li><strong>Cycle:</strong> review monthly; keep what works; adjust what doesn’t</li>
              </ul>
            </section>

            {/* Toolkits */}
            <section id="toolkits" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6 space-y-6">
              <h2 className="text-2xl font-bold mb-2">Quick Toolkits You Can Use Today</h2>
              <div>
                <h3 className="text-xl font-semibold">The 7-Day Reset (student)</h3>
                <p className="text-gray-200">Plan your week (3 must-dos), fix sleep times, add two 20-minute walks, one past paper, two peer-teach sessions, and nightly wind-down.</p>
              </div>
              <div>
                <h3 className="text-xl font-semibold">Build a Calm Kit (backpack-friendly)</h3>
                <p className="text-gray-200">Earplugs/headphones, stress ball, water bottle, cue card with breathing prompts, mini mindfulness script, “3 people I can text” list.</p>
              </div>
              <div>
                <h3 className="text-xl font-semibold">Parent/Educator Micro-Playbook (scripts)</h3>
                <ul className="list-disc pl-6 space-y-1 text-gray-200 mt-2">
                  <li><strong>Notice & Invite:</strong> “I’ve noticed you seem tense. Want to make this week easier together?”</li>
                  <li><strong>Normalize & Plan:</strong> “Stress is common before exams. Let’s try one coping mechanism tonight and review tomorrow.”</li>
                  <li><strong>Boundary & Choice:</strong> “Two 25-minute study blocks or one 40-minute? Your call.”</li>
                  <li><strong>Escalate Kindly:</strong> “If this keeps feeling heavy, the school counselor can help, shall I walk with you?”</li>
                </ul>
              </div>
            </section>

            {/* FAQs */}
            <section id="faqs" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">FAQs (add FAQPage schema)</h2>
              <ol className="list-decimal pl-6 space-y-3 text-gray-200">
                <li>
                  <p className="font-semibold">What’s the difference between normal stress and an anxiety disorder in students?</p>
                  <p>Stress is a proportionate response to a demand; anxiety persists and generalizes, often with physical symptoms and avoidance. If it lasts 2+ weeks or disrupts daily life, seek support.</p>
                </li>
                <li>
                  <p className="font-semibold">How can CBT help with test anxiety?</p>
                  <p>CBT teaches you to spot unhelpful thoughts (“I’ll fail everything”) and swap them for balanced beliefs, paired with exposure to timed practice. Confidence rises as catastrophizing falls.</p>
                </li>
                <li>
                  <p className="font-semibold">What are quick coping mechanisms before an exam?</p>
                  <p>Box breathing (1–3 minutes), the 5-4-3-2-1 grounding scan, and a 2-minute positive self-talk script. Pair with a simple snack and water.</p>
                </li>
                <li>
                  <p className="font-semibold">What is study-life balance and how do I set it up?</p>
                  <p>A weekly rhythm that protects sleep, relationships, and recovery. Start with three focused study blocks per subject, two evenings off, and a weekend review.</p>
                </li>
                <li>
                  <p className="font-semibold">How do school counselors support mental health?</p>
                  <p>They triage concerns, provide brief interventions (study skills, coping mechanisms), liaise with teachers, and refer to a school psychologist or therapist for intensive care.</p>
                </li>
                <li>
                  <p className="font-semibold">What are reasonable adjustments for ADHD or autism in exams?</p>
                  <p>Extra time, rest breaks, quiet rooms, assistive tech, or alternative formats, agreed in advance with the school.</p>
                </li>
                <li>
                  <p className="font-semibold">How should a school respond if a student discloses self-harm?</p>
                  <p>Ensure immediate safety, follow safeguarding policy, involve guardians as appropriate, and offer a warm handoff to counseling services or external care.</p>
                </li>
                <li>
                  <p className="font-semibold">Does screen time at night impact sleep and learning?</p>
                  <p>Yes, blue light and emotional arousal can delay sleep. A 60-minute phone-free buffer improves sleep hygiene and next-day focus.</p>
                </li>
                <li>
                  <p className="font-semibold">Are mindfulness practices safe and evidence-based for teens?</p>
                  <p>Brief, age-appropriate mindfulness and breathing exercises are generally safe and can reduce anxiety and improve attention when used consistently.</p>
                </li>
              </ol>
            </section>

            {/* Help / CTA */}
            <section id="help" style={{ scrollMarginTop: "7rem" }} className="bg-gradient-to-r from-rose-500/20 to-indigo-500/20 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-2">How we help</h2>
              <p className="text-gray-200">
                As a wellbeing-first companion to this guide, Infinite Solution offers ethical, skills-building academic support that reduces stress, anxiety, and exam pressure without replacing your learning: coaching for assignments, essays, dissertations, theses, and reports; help with outlining, research synthesis, and citation styles; expert editing and originality checks to keep work plagiarism-free and on time; and practical study plans that protect sleep hygiene, digital wellbeing, and study-life balance.
              </p>
              <p className="text-gray-200 mt-3">
                Our mentors use light CBT-inspired planning, micro-mindfulness breaks, and weekly check-ins to build resilience and confidence while encouraging appropriate help-seeking with your school counselor whenever deeper support is needed.
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <a href="/Contactus" className="inline-flex items-center px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 font-semibold">
                  Get Help Now
                </a>
                <a href="https://wa.me/917980085981" className="inline-flex items-center px-4 py-2 rounded-lg border border-white/20 hover:bg-white/10">
                  Talk to a Specialist
                </a>
              </div>
            </section>

            {/* Conclusion */}
            <section id="conclusion" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Conclusion: Small Steps, Big Payoff</h2>
              <p className="text-gray-200">
                Mental Health in Education is not a side quest, it’s the engine of learning. Blend strong sleep hygiene, regular physical activity, practical coping mechanisms like CBT and mindfulness, and smarter study-life balance. Schools amplify this by embedding mental health literacy, UDL, and a clear MTSS pathway to a school counselor, school psychologist, or counseling services. Start with one change this week—earlier bedtime, a 5-minute breathing habit, or a realistic study plan—and build resilience one small win at a time.
              </p>
            </section>

            import { FaInstagram } from "react-icons/fa";

<section
  id="acknowledgement"
  style={{ scrollMarginTop: "7rem" }}
  className="bg-gradient-to-r from-indigo-900/40 via-purple-900/30 to-pink-900/20 rounded-xl border border-white/10 p-6 mt-10 shadow-lg"
>
  <h2 className="text-2xl sm:text-3xl font-extrabold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-indigo-300 pb-2">
    Acknowledgement
  </h2>

  <p className="text-gray-200">
    The concept was first introduced by{" "}
    <span className="font-semibold text-white">Abhishek Shaw</span>, who set the foundation for the content’s direction.
    <br />
    <a
      href="https://www.instagram.com/abhishekshawofficial_?igsh=Ynh2OXl6bm4xN3Z4"
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 mt-2 text-pink-400 hover:text-pink-300 font-semibold"
    >
      <FaInstagram /> @abhishekshaw
    </a>
  </p>

  <p className="text-gray-200 mt-4">
    To shape the initial draft, we utilized advanced AI tools like <span className="font-semibold">ChatGPT</span> and{" "}
    <span className="font-semibold">Google Gemini</span>, ensuring speed, structure, and creativity. Once the draft was
    developed, our team at{" "}
    <a
      href="https://www.infinitesolutionedu.com"
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-400 font-semibold hover:text-blue-300 underline"
    >
      Infinite Solution
    </a>{" "}
    took over to polish it, optimizing for SEO, refining the tone for clarity, and aligning the message with the target
    audience.
  </p>

  <p className="text-gray-200 mt-4">
    This collaborative approach seamlessly blended human expertise with AI-driven efficiency, resulting in content that
    is impactful, high-performing, and highly relevant to readers.
  </p>
</section>

          </article>
        </div>
      </div>
    </section>
  );
}
